import { RDUEComplianceStatus } from "@tfg/db"
import { z } from "zod"

import {
  CountryIso3166Schema,
  LanguageIso6391Schema,
} from "@tfg/types/schemas/enum"
import { dateFilterDtoSchema } from "@tfg/types/types/date-filter.dto"

export const fetchDataOptionsSchema = z.object({
  skip: z.coerce.number(),
  take: z.coerce.number(),
})
export type fetchDataOptionsValidationType = z.infer<
  typeof fetchDataOptionsSchema
>

export type fetchDataOptionsParam = {
  fetchDataOptions: fetchDataOptionsValidationType
}

const transformAgecCompliance = (
  value: string[] | undefined
): (boolean | null)[] => {
  if (!value) return []

  return Array.from(
    new Set(
      value.map((item) => {
        if (item === "yes") return true
        if (item === "no") return false
        return null
      })
    )
  )
}

export const filtersSchema = z
  .object({
    search: z.string(),
    agecCompliance: z
      .array(z.enum(["yes", "no", "not-applicable"]))
      .transform(transformAgecCompliance),
    type: z.array(z.string()),
    collection: z.array(z.string()),
    supplierContact: z.array(z.string()),
    brand: z.array(z.string()),
    standardId: z.array(z.coerce.number()),
    organizationId: z.array(z.uuid()),
    country: z.array(CountryIso3166Schema),
    incomingLaunchDate: z.enum(["incoming", "expired", "all"]),
    ingestionDate: dateFilterDtoSchema,
  })
  .partial()
export type filtersValidationType = z.infer<typeof filtersSchema>

// Schema for API DTOs with loose string types (matches generated DTO types)
export const filtersDtoSchema = z
  .object({
    search: z.string().optional(),
    agecCompliance: z
      .array(z.enum(["yes", "no", "not-applicable"]))
      .optional()
      .transform(transformAgecCompliance),
    type: z.array(z.string()).optional(),
    collection: z.array(z.string()).optional(),
    supplierContact: z.array(z.string()).optional(),
    brand: z.array(z.string()).optional(),
    standardId: z.array(z.number()).optional(),
    organizationId: z.array(z.string()).optional(),
    country: z.array(z.string()).optional(),
    incomingLaunchDate: z.string().optional(),
    ingestionDate: dateFilterDtoSchema.optional(),
  })
  .partial()
export type filtersDtoValidationType = z.infer<typeof filtersDtoSchema>

// Schema for RDUE API DTOs with loose string types (matches generated DTO types + rdueCompliance)
export const filtersRdueDtoSchema = z
  .object({
    search: z.string().optional(),
    rdueCompliance: z.array(z.string()).optional(),
    type: z.array(z.string()).optional(),
    collection: z.array(z.string()).optional(),
    brand: z.array(z.string()).optional(),
    organizationId: z.array(z.uuid()).optional(),
    country: z.array(CountryIso3166Schema).optional(),
  })
  .partial()
export type filtersRdueDtoValidationType = z.infer<typeof filtersRdueDtoSchema>

export const filtersRdueSchema = z.object({
  search: z.string().optional(),
  rdueCompliance: z.array(z.enum(RDUEComplianceStatus)).optional(),
  type: z.array(z.string()).optional(),
  collection: z.array(z.string()).optional(),
  brand: z.array(z.string()).optional(),
  organizationId: z.array(z.uuid()).optional(),
  country: z.array(CountryIso3166Schema).optional(),
})
export type filtersRdueValidationType = z.infer<typeof filtersRdueSchema>

export const getProductsSchema = z.object({
  fetchDataOptions: fetchDataOptionsSchema,
  filters: filtersSchema.optional(),
})
export type getProductsValidationType = z.infer<typeof getProductsSchema>

export const getProductsRdueSchema = z.object({
  fetchDataOptions: fetchDataOptionsSchema,
  filters: filtersRdueSchema.optional(),
})
export type getProductsRdueValidationType = z.infer<typeof getProductsSchema>

export const getProductsExportSchema = z.object({
  filters: filtersDtoSchema.optional(),
})

export type getProductsExportValidationType = z.infer<
  typeof getProductsExportSchema
>

export const acceptLanguageHeaderSchema = z.object({
  "accept-language": LanguageIso6391Schema,
})

export type acceptLanguageHeaderType = z.infer<
  typeof acceptLanguageHeaderSchema
>

export const exportCertificatesSchema = z.object({
  filters: filtersDtoSchema.optional(),
})
export type exportCertificatesValidationType = z.infer<
  typeof exportCertificatesSchema
>

export const updateAgecShowSchema = z.object({
  agecShow: z.boolean(),
})
export type updateAgecShowValidationType = z.infer<typeof updateAgecShowSchema>

export const bulkUpdateAgecShowSchema = z.object({
  productIds: z.array(z.uuid()),
  agecShow: z.boolean(),
})
export type bulkUpdateAgecShowValidationType = z.infer<
  typeof bulkUpdateAgecShowSchema
>

export const bulkLaunchEcobalyseAnalysisSchema = z.union([
  z.object({
    productIds: z.undefined(),
    organizationId: z.uuid(),
  }),
  z.object({
    productIds: z.array(z.number()),
    organizationId: z.undefined(),
  }),
])
export type bulkLaunchEcobalyseAnalysisType = z.infer<
  typeof bulkLaunchEcobalyseAnalysisSchema
>
